import React, { useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import StickyNote from "./StickyNote";
import MinimizedTray from "./MinimizedTray";
import {
  COLUMN_WIDTH,
  COLUMN_GAP,
  NOTE_GAP,
} from "./constants";

export default function PinBoard({
  columns,
  loading,
  onReorder,
  onEdit,
  onDelete,
  onComplete,
  onUncomplete,
  onCharged,
  onChangeCategory,
  onToggleRead,
  onSetInProgress,
  onMinimize,
  onPrint,
  onPrintLabel,
  onUpdateField,
  onPartialExecute,
  onOpenContract,
  isMobile,
  types,
  channels,
  people,
  minimizedNotes,
  onToggleMinimizeAll,
  isAllMinimized,
}) {
  const boardRef = useRef(null);
  const noteRefs = useRef({});
  const trayRef = useRef(null);
  const [isAnyDragging, setIsAnyDragging] = React.useState(false);

  const isOverTray = useCallback((clientX, clientY) => {
    const el = trayRef.current;
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return (
      clientX >= r.left &&
      clientX <= r.right &&
      clientY >= r.top &&
      clientY <= r.bottom
    );
  }, []);

  // Compute target column/order from pointer position
  const computeDropTarget = useCallback(
    (clientX, clientY, draggedId) => {
      const board = boardRef.current;
      if (!board) return null;
      const rect = board.getBoundingClientRect();
      const relX = clientX - rect.left + board.scrollLeft;
      const relY = clientY - rect.top + board.scrollTop;
      const stride = COLUMN_WIDTH + COLUMN_GAP;
      let targetCol = Math.max(0, Math.floor(relX / stride));
      if (targetCol > columns.length) targetCol = columns.length;

      const colNotes = (columns[targetCol] || []).filter(
        (n) => n.id !== draggedId
      );

      // Find insertion index by measuring note tops
      let targetOrder = colNotes.length;
      for (let i = 0; i < colNotes.length; i++) {
        const el = noteRefs.current[colNotes[i].id];
        if (!el) continue;
        const elRect = el.getBoundingClientRect();
        const midY = elRect.top + elRect.height / 2 - rect.top + board.scrollTop;
        if (relY < midY) {
          targetOrder = i;
          break;
        }
      }
      return { column: targetCol, order: targetOrder };
    },
    [columns]
  );

  const totalColumns = Math.max(columns.length, 1);
  const boardWidth = totalColumns * (COLUMN_WIDTH + COLUMN_GAP) + COLUMN_GAP;

  return (
    <div
      ref={boardRef}
      className="relative w-full overflow-auto board-bg"
      style={{ minHeight: "calc(100vh - 180px)" }}
      data-testid="pinboard-canvas"
    >
      {loading && (
        <div
          className="absolute inset-0 flex items-center justify-center text-ink-muted"
          data-testid="board-loading"
        >
          <p className="font-caveat text-3xl">ΦΟΡΤΩΣΗ...</p>
        </div>
      )}

      {!loading && columns.every((c) => c.length === 0) && minimizedNotes.length === 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-ink-muted pointer-events-none px-6">
          <p className="font-caveat text-4xl text-ink/70">Ο ΠΙΝΑΚΑΣ ΕΙΝΑΙ ΑΔΕΙΟΣ</p>
          <p className="text-xs uppercase tracking-[0.2em] font-bold">
            ΠΑΤΗΣΕ «ΝΕΑ ΚΑΤΑΧΩΡΗΣΗ» ΓΙΑ ΝΑ ΞΕΚΙΝΗΣΕΙΣ
          </p>
        </div>
      )}

      <LayoutGroup>
        {/* Minimized tray ABOVE the notes — always rendered as drop target */}
        <MinimizedTray
          ref={trayRef}
          notes={minimizedNotes}
          onRestore={onMinimize}
          onEdit={onEdit}
          types={types}
          highlightDrop={isAnyDragging}
          onToggleMinimizeAll={onToggleMinimizeAll}
          isAllMinimized={isAllMinimized}
        />

        <div
          className={isMobile ? "relative flex flex-col items-stretch gap-3 px-3 py-3 pb-32" : "relative flex items-start gap-[18px] p-6 pb-32"}
          style={isMobile ? undefined : { minWidth: `${boardWidth}px` }}
          data-testid="columns-wrapper"
        >
          {isMobile ? (
            // Mobile: flatten all columns into a single full-width stack
            <AnimatePresence>
              {columns.flat().map((note) => (
                <motion.div
                  key={note.id}
                  layout
                  layoutId={note.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  className="w-full"
                >
                  <StickyNote
                    note={note}
                    isMobile
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onComplete={onComplete}
                    onUncomplete={onUncomplete}
                    onCharged={onCharged}
                    onChangeCategory={onChangeCategory}
                    onToggleRead={onToggleRead}
                    onSetInProgress={onSetInProgress}
                    onMinimize={onMinimize}
                    onPrint={onPrint}
                    onPrintLabel={onPrintLabel}
                    onUpdateField={onUpdateField}
                    onPartialExecute={onPartialExecute}
                    onOpenContract={onOpenContract}
                    types={types}
                    channels={channels}
                    people={people}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          ) : (
            columns.map((colNotes, colIdx) => (
            <div
              key={colIdx}
              data-testid={`column-${colIdx}`}
              className="flex flex-col gap-[14px] shrink-0"
              style={{ width: `${COLUMN_WIDTH}px` }}
            >
              {colNotes.length === 0 && colIdx > 0 && (
                <div className="w-full h-32 rounded-lg border-2 border-dashed border-ink/10 flex items-center justify-center text-ink/30 text-[10px] uppercase tracking-widest font-bold">
                  ΑΦΗΣΕ ΕΔΩ
                </div>
              )}
              <AnimatePresence>
                {colNotes.map((note) => (
                  <motion.div
                    key={note.id}
                    layout
                    layoutId={note.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{
                      type: "spring",
                      stiffness: 380,
                      damping: 32,
                    }}
                    ref={(el) => {
                      if (el) noteRefs.current[note.id] = el;
                      else delete noteRefs.current[note.id];
                    }}
                  >
                    <StickyNote
                      note={note}
                      onDragStart={() => setIsAnyDragging(true)}
                      onDragEnd={(clientX, clientY) => {
                        setIsAnyDragging(false);
                        if (isOverTray(clientX, clientY)) {
                          onMinimize(note);
                          return;
                        }
                        const target = computeDropTarget(
                          clientX,
                          clientY,
                          note.id
                        );
                        if (target) onReorder(note.id, target.column, target.order);
                      }}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onComplete={onComplete}
                      onUncomplete={onUncomplete}
                      onCharged={onCharged}
                      onChangeCategory={onChangeCategory}
                      onToggleRead={onToggleRead}
                      onSetInProgress={onSetInProgress}
                      onMinimize={onMinimize}
                      onPrint={onPrint}
                      onPrintLabel={onPrintLabel}
                      onUpdateField={onUpdateField}
                      onPartialExecute={onPartialExecute}
                      onOpenContract={onOpenContract}
                      types={types}
                      channels={channels}
                      people={people}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ))
          )}
        </div>
      </LayoutGroup>
    </div>
  );
}
